import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy, AfterViewInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, takeUntil, startWith } from 'rxjs';
import dayjs, { Dayjs } from 'dayjs/esm';
import { WizardCoachOverlayUiService } from '../../services/wizard-coach-overlay-ui.service';
import { parseLocalCalendarDate } from 'src/app/shared/utils/parse-local-calendar-date';
import { MatDialog } from '@angular/material/dialog';
import { WTimeDialogComponent } from '@candidosales/material-time-picker';


@Component({
  selector: 'app-datepicker1',
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss'
})
export class DatepickerComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly coachUi = inject(WizardCoachOverlayUiService);

  @Input({ required: true }) placeholder!: string;
  @Input({ required: true }) control!: FormControl;
  @Input({ required: false }) isTimeOnly = false;
  @Input({ required: false }) isDateTime = false;
  /**
   * ngx-daterangepicker-material aligns min/max with its UTC-mode calendar only when
   * bounds are passed as strings (it uses dayjs(str).utc(true)). Passing Dayjs objects
   * skips that and causes off-by-one vs calendar cells.
   */
  @Input({ required: false }) set minDate(value: string | Date | Dayjs | null) {
    const parsed = parseLocalCalendarDate(value);
    this.hasMinDateBound = !!parsed;
    this.boundMinDayjs = parsed;
    this.pickerMinStr = parsed ? parsed.format('YYYY-MM-DD') : null;
  }
  @Input({ required: false }) set maxDate(value: string | Date | Dayjs | null) {
    const parsed = parseLocalCalendarDate(value);
    this.hasMaxDateBound = !!parsed;
    this.boundMaxDayjs = parsed;
    this.pickerMaxStr = parsed ? parsed.format('YYYY-MM-DD') : null;
  }
  /**
   * 'auto' = open up by default; when wizard coach overlay is active, flip away from coach
   * (same signal as ng-select {@link WizardCoachOverlayUiService.resolveNgSelectDropdownPosition}).
   */
  @Input({ required: false }) drops: 'auto' | 'up' | 'down' = 'auto';

  value: any;
  /** Passed to ngx-daterangepicker as strings so its UTC calendar matches bounds. */
  pickerMinStr: string | null = null;
  pickerMaxStr: string | null = null;
  private boundMinDayjs: Dayjs | null = null;
  private boundMaxDayjs: Dayjs | null = null;
  hasMinDateBound = false;
  hasMaxDateBound = false;
  datePickerControl: FormControl; // Internal control that holds TimePeriod format for the directive
  private destroy$ = new Subject<void>();

  resolvedDrops(): 'up' | 'down' {
    return this.coachUi.resolveDatepickerDrops(this.drops);
  }

  constructor(private cdr: ChangeDetectorRef, private dialog: MatDialog) {
    // Create internal control for datepicker directive
    this.datePickerControl = new FormControl(null);
  }

  ngOnInit(): void {
    // Initialize value from control's current value first
    if (!this.isTimeOnly) {
      if (this.control.value) {
        this.syncControlToDatePicker();
      } else {
        this.value = null;
        this.datePickerControl.setValue(null);
      }
    } else {
      // For time-only, initialize immediately if value exists
      const initialValue = this.control.value;
      if (initialValue && initialValue !== '' && initialValue !== null && initialValue !== undefined) {
        this.initializeValueFromString(String(initialValue));
      } else {
        this.value = null;
      }
    }

    // Sync datePickerControl to main control when datepicker changes
    this.datePickerControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(pickerValue => {
        if (pickerValue && typeof pickerValue === 'object' && pickerValue.startDate) {
          const start = pickerValue.startDate.format('YYYY-MM-DD');
          if (this.isDateTime) {
            const formatted = pickerValue.startDate.format('YYYY-MM-DD HH:mm');
            this.control.setValue(formatted, { emitEvent: false });
          } else {
            this.control.setValue(start, { emitEvent: false });
          }
        } else if (!pickerValue) {
          this.control.setValue(null, { emitEvent: false });
        }
      });

    // Sync main control to datePickerControl when external value changes
    // Use startWith to catch initial value and subsequent changes
    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        if (this.isTimeOnly) {
          // Check if value is null/undefined/empty first (before type checking)
          if (value === null || value === undefined || value === '') {
            this.value = null;
            this.cdr.detectChanges();
            return;
          }
          
          // Handle time string
          if (typeof value === 'string') {
            const timeStr = String(value).trim();
            // Only update if it's a valid time string
            if (timeStr.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
              this.initializeValueFromString(timeStr);
            } else {
              // Don't set to null if we already have a valid value
              if (!this.value || this.value === null) {
                this.value = null;
                this.cdr.detectChanges();
              }
            }
          } else if (value instanceof Date) {
            // Handle Date object - convert to HH:mm:ss format
            const hours = value.getHours().toString().padStart(2, '0');
            const minutes = value.getMinutes().toString().padStart(2, '0');
            const seconds = value.getSeconds().toString().padStart(2, '0');
            this.initializeValueFromString(`${hours}:${minutes}:${seconds}`);
          }
          return;
        }

        // Skip if value is already a TimePeriod object (from datepicker)
        if (value && typeof value === 'object' && value.startDate) {
          return; // Already handled by datePickerControl.valueChanges
        }

        // Convert string to TimePeriod format for datepicker
        // Only sync if the value is different to prevent infinite loops
        if (value && typeof value === 'string') {
          const currentPickerValue = this.datePickerControl.value;
          const parsedDate = this.parseStringToDayjs(value);
          
          if (parsedDate) {
            const shouldSync = !currentPickerValue || 
                             !currentPickerValue.startDate || 
                             !currentPickerValue.startDate.isSame(parsedDate);
            
            if (shouldSync) {
              this.syncControlToDatePicker();
            }
          }
        } else if (!value) {
          this.datePickerControl.setValue(null, { emitEvent: false });
          this.value = null;
          this.cdr.detectChanges();
        }
      });

    // Sync disabled state
    if (this.control.disabled) {
      this.datePickerControl.disable();
    }
    this.control.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.control.disabled) {
          this.datePickerControl.disable();
        } else {
          this.datePickerControl.enable();
        }
      });

    // Handle internal timepicker revert/reset button click
    // The library doesn't emit an event on revert, but it closes the dialog with -1.
    // We listen to all dialogs and check if it's the timepicker dialog returning -1.
    this.dialog.afterOpened
      .pipe(takeUntil(this.destroy$))
      .subscribe((ref) => {
        if (ref.componentInstance instanceof WTimeDialogComponent) {
          ref.afterClosed().subscribe((result) => {
            if (result === -1) {
              this.resetPicker();
            }
          });
        }
      });
  }

  ngAfterViewInit(): void {
    // Re-check value after view init in case form was patched after ngOnInit
    // Use setTimeout to ensure form patching has completed
    setTimeout(() => {
      if (this.isTimeOnly) {
        const currentValue = this.control.value;
        if (currentValue && currentValue !== '' && currentValue !== null && currentValue !== undefined) {
          const timeStr = String(currentValue).trim();
          // Always re-initialize if we have a valid time string and value is null or different
          if (timeStr && timeStr.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
            // Check if we need to update
            const needsUpdate = !this.value || 
                               this.value === null || 
                               (this.value.hour === '0' && this.value.minute === '0' && this.value.meriden === 'PM');
            if (needsUpdate) {
              this.initializeValueFromString(timeStr);
            }
          }
        }
      }
    }, 100); // Small delay to ensure form patching completes
  }

  private syncControlToDatePicker(): void {
    if (!this.control.value || this.isTimeOnly) return;

    const dateString = this.control.value;
    const parsedDate = this.parseStringToDayjs(dateString);
    
    if (parsedDate && dayjs.isDayjs(parsedDate)) {
      const timePeriod = {
        startDate: parsedDate,
        endDate: parsedDate
      };
      this.datePickerControl.setValue(timePeriod, { emitEvent: false });
      this.value = parsedDate;
    } else {
      this.datePickerControl.setValue(null, { emitEvent: false });
      this.value = null;
    }
    this.cdr.detectChanges();
  }

  private parseStringToDayjs(dateString: string): dayjs.Dayjs | null {
    if (!dateString) return null;

    if (!this.isTimeOnly) {
      if (this.isDateTime) {
        // Handle datetime format: "YYYY-MM-DD HH:mm" or "YYYY-MM-DDTHH:mm:ss"
        let parsedDate: dayjs.Dayjs;
        if (dateString.includes('T')) {
          parsedDate = dayjs(dateString);
        } else if (dateString.includes(' ')) {
          parsedDate = dayjs(dateString, 'YYYY-MM-DD HH:mm');
        } else {
          // Date only, assume midnight
          parsedDate = dayjs(dateString).startOf('day');
        }
        return parsedDate.isValid() ? parsedDate : null;
      } else {
        // Handle date-only format: "YYYY-MM-DD"
        const parsedDate = dayjs(dateString);
        return parsedDate.isValid() ? parsedDate : null;
      }
    } else {
      // For time-only, return the original value structure
      return null; // Will be handled by initializeValueFromString
    }
  }

  private isSameValue(oldValue: any, newValue: any): boolean {
    if (oldValue === newValue) return true;
    if (!oldValue || !newValue) return false;
    
    if (dayjs.isDayjs(oldValue) && dayjs.isDayjs(newValue)) {
      return oldValue.isSame(newValue);
    }
    
    return false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeValueFromString(dateString: string): void {
    if (!dateString || dateString === '' || dateString === null || dateString === undefined) {
      this.value = null;
      this.cdr.detectChanges();
      return;
    }

    // Convert to string if it's not already
    const timeStr = String(dateString).trim();

    if (this.isTimeOnly) {
      // Handle time-only format: "HH:mm:ss" or "HH:mm"
      // Parse the time string - handle both "HH:mm:ss" and "HH:mm" formats
      const timeParts = timeStr.split(':');
      
      if (timeParts.length < 2) {
        this.value = null;
        this.cdr.detectChanges();
        return;
      }

      const hour = parseInt(timeParts[0], 10);
      const minute = parseInt(timeParts[1] || '0', 10);
      
      // Validate the time values
      if (isNaN(hour) || isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        this.value = null;
        this.cdr.detectChanges();
        return;
      }
      
      // Convert to 12-hour format
      const meriden = hour >= 12 ? 'PM' : 'AM';
      let adjustedHour = hour % 12;
      if (adjustedHour === 0) {
        adjustedHour = 12; // 0 or 12 both become 12
      }

      // Material-timepicker format: hour and minute as strings (unpadded)
      // minute: '0' for 0, '5' for 5, '30' for 30 (just the number, no leading zeros)
      const newValue = {
        hour: String(adjustedHour),
        minute: String(minute), // Convert number to string directly: 0 -> '0', 30 -> '30'
        meriden,
        format: 12
      };
      
      this.value = newValue;
    }
    
    this.cdr.detectChanges();
  }

  onTimeChange(event: any): void {
    if (this.control.disabled) return;

    const meridiem = event.meriden;
    let hourNum = parseInt(event.hour, 10);

    if (meridiem === 'PM' && hourNum < 12) {
      hourNum += 12;
    } else if (meridiem === 'AM' && hourNum === 12) {
      hourNum = 0;
    }

      const hour = String(hourNum).padStart(2, '0');
    // Extract minute - might be padded or not, just get the numeric value
    const minuteNum = parseInt(String(event.minute).replace(/^0+/, '') || '0', 10);
    const minute = String(minuteNum).padStart(2, '0');

    const isoString = `${hour}:${minute}:00`;

    // Use emitEvent: false to prevent triggering valueChanges subscription
    // We'll update the value directly
    this.control.setValue(isoString, { emitEvent: false });
    // Manually update our internal value - use single digit strings like the component expects
    this.value = {
      hour: event.hour,
      minute: String(minuteNum), // Single digit string, not padded
      meriden: event.meriden,
      format: 12
    };
    this.cdr.detectChanges();
  }

  onValueChange(event: any): void {
    // This is handled by datePickerControl.valueChanges
    // Just ensure the value is synced and keep value within optional bounds.
    if (event && event.startDate) {
      const pickedStr = event.startDate.format('YYYY-MM-DD');
      const minStr = this.boundMinDayjs?.format('YYYY-MM-DD');
      const maxStr = this.boundMaxDayjs?.format('YYYY-MM-DD');
      if (this.hasMinDateBound && minStr && pickedStr < minStr && this.boundMinDayjs) {
        const d = this.boundMinDayjs;
        this.datePickerControl.setValue({ startDate: d, endDate: d }, { emitEvent: true });
        return;
      }
      if (this.hasMaxDateBound && maxStr && pickedStr > maxStr && this.boundMaxDayjs) {
        const d = this.boundMaxDayjs;
        this.datePickerControl.setValue({ startDate: d, endDate: d }, { emitEvent: true });
        return;
      }
      this.value = event.startDate;
    }
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const cursorPos = input.selectionStart || 0;
    
    // Extract only digits
    const digits = value.replace(/\D/g, '');
    
    // If we have digits and the value doesn't match the expected format, format it
    if (digits.length > 0) {
      let formatted = '';
      
      // Format as DD-MM-YYYY
      if (digits.length <= 2) {
        formatted = digits;
      } else if (digits.length <= 4) {
        formatted = digits.substring(0, 2) + '-' + digits.substring(2);
      } else if (digits.length <= 8) {
        formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4);
      } else {
        formatted = digits.substring(0, 2) + '-' + digits.substring(2, 4) + '-' + digits.substring(4, 8);
      }
      
      // Only update if formatted value is different
      if (formatted !== value) {
        input.value = formatted;
        
        // Adjust cursor position after formatting
        let newPos = cursorPos;
        if (cursorPos === 2 && digits.length > 2) {
          newPos = 3; // Move past the first dash
        } else if (cursorPos === 5 && digits.length > 4) {
          newPos = 6; // Move past the second dash
        } else {
          newPos = Math.min(cursorPos + (formatted.length - value.length), formatted.length);
        }
        input.setSelectionRange(newPos, newPos);
        
        // Try to parse and update the datepicker control when complete
        if (formatted.length === 10) {
          const parts = formatted.split('-');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            const year = parseInt(parts[2], 10);
            
            if (!isNaN(day) && !isNaN(month) && !isNaN(year) && day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
              const date = dayjs(`${year}-${month + 1}-${day}`, 'YYYY-M-D');
              if (date.isValid()) {
                const timePeriod = {
                  startDate: date,
                  endDate: date
                };
                this.datePickerControl.setValue(timePeriod, { emitEvent: true });
              }
            }
          }
        }
      }
    }
  }

  onDateTimeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    // Only format if user is typing numbers
    if (value && /^\d+$/.test(value.replace(/[^\d]/g, ''))) {
      const digits = value.replace(/[^\d]/g, '');
      let formatted = '';
      
      if (digits.length > 0) {
        // Format date part (DD-MM-YYYY)
        formatted = digits.substring(0, 2);
        if (digits.length > 2) {
          formatted += '-' + digits.substring(2, 4);
          if (digits.length > 4) {
            formatted += '-' + digits.substring(4, 8);
            // Format time part (HH:mm)
            if (digits.length > 8) {
              formatted += ' ' + digits.substring(8, 10);
              if (digits.length > 10) {
                formatted += ':' + digits.substring(10, 12);
              }
            }
          }
        }
      }
      
      // Update the input display value
      if (formatted !== value) {
        const cursorPos = input.selectionStart || 0;
        input.value = formatted;
        
        // Try to maintain cursor position
        const newPos = Math.min(cursorPos, formatted.length);
        input.setSelectionRange(newPos, newPos);
        
        // Try to parse and update the datepicker control
        if (formatted.length >= 10) {
          const dateTimeParts = formatted.split(' ');
          if (dateTimeParts.length >= 1) {
            const dateParts = dateTimeParts[0].split('-');
            if (dateParts.length === 3) {
              const day = parseInt(dateParts[0], 10);
              const month = parseInt(dateParts[1], 10) - 1;
              const year = parseInt(dateParts[2], 10);
              
              let hour = 0;
              let minute = 0;
              if (dateTimeParts.length > 1 && dateTimeParts[1].includes(':')) {
                const timeParts = dateTimeParts[1].split(':');
                hour = parseInt(timeParts[0] || '0', 10);
                minute = parseInt(timeParts[1] || '0', 10);
              }
              
              if (!isNaN(day) && !isNaN(month) && !isNaN(year) && day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900) {
                const date = dayjs(`${year}-${month + 1}-${day} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`, 'YYYY-M-D HH:mm');
                if (date.isValid()) {
                  const timePeriod = {
                    startDate: date,
                    endDate: date
                  };
                  this.datePickerControl.setValue(timePeriod, { emitEvent: true });
                }
              }
            }
          }
        }
      }
    }
  }

  resetPicker(): void {
    this.control.setValue(null);
    this.datePickerControl.setValue(null);
    this.value = null;
    this.cdr.detectChanges();
  }
}